import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DiffPreviewDialog } from '@/components/layout/diff-preview-dialog'
import { fetchWithAuth } from '@/lib/api'
import { approveSoulProposal, dismissSoulProposal } from '@/lib/api/soul-proposals'
import { testIds } from '@/lib/test-ids'
import type { Thread } from './types'

type SoulProposalMeta = {
  kind?: unknown
  threadKind?: unknown
  target_path?: unknown
  target_document_id?: unknown
  rationale?: unknown
  proposed_patch?: {
    old_string?: unknown
    new_string?: unknown
  }
  resolved?: unknown
}

function isSoulProposalMeta(meta: SoulProposalMeta | undefined): boolean {
  if (!meta) return false
  return meta.kind === 'soul_proposal' || meta.threadKind === 'soul_proposal'
}

function readMeta(thread: Thread): {
  targetPath: string
  documentId: string
  filename: string
  rationale: string
  oldString: string
  newString: string
} | null {
  const meta = thread.metadata as SoulProposalMeta | undefined
  if (!isSoulProposalMeta(meta) || !meta) return null
  const targetPath = typeof meta.target_path === 'string' ? meta.target_path : ''
  const documentId = typeof meta.target_document_id === 'string' ? meta.target_document_id : ''
  const oldString =
    typeof meta.proposed_patch?.old_string === 'string' ? meta.proposed_patch.old_string : ''
  const newString =
    typeof meta.proposed_patch?.new_string === 'string' ? meta.proposed_patch.new_string : ''
  if (!targetPath || !documentId || !oldString) return null
  const parts = targetPath.split('/').filter(Boolean)
  const filename = parts.length ? (parts[parts.length - 1] as string) : targetPath
  const rationale = typeof meta.rationale === 'string' ? meta.rationale : ''
  return { targetPath, documentId, filename, rationale, oldString, newString }
}

function applyPatch(content: string, oldString: string, newString: string): string {
  if (!content.includes(oldString)) return content
  return content.replace(oldString, newString)
}

export function SoulProposalActionBar({
  thread,
  onResolved,
  /** `banner` = top chrome strip; `inline` = under proposal body in the reader. */
  variant = 'banner',
}: {
  thread: Thread
  onResolved?: () => void
  variant?: 'banner' | 'inline'
}) {
  const parsed = readMeta(thread)
  const [diffOpen, setDiffOpen] = useState(false)
  const [currentContent, setCurrentContent] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const loadDiffContent = useCallback(async () => {
    if (!parsed) return
    setLoadError(null)
    try {
      const docRes = await fetchWithAuth(`/api/documents/${parsed.documentId}`)
      if (!docRes.ok) {
        const err = await docRes.json().catch(() => ({}))
        throw new Error((err as { error?: string }).error ?? 'Could not load soul document')
      }
      const docJson = (await docRes.json()) as { content?: string }
      if (typeof docJson.content !== 'string') {
        throw new Error('Document response missing content')
      }
      setCurrentContent(docJson.content)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not load diff'
      setLoadError(msg)
      toast.error(msg)
    }
  }, [parsed])

  const handleOpenDiff = useCallback(async () => {
    setDiffOpen(true)
    await loadDiffContent()
  }, [loadDiffContent])

  const handleApprove = useCallback(async () => {
    if (!parsed) return
    setBusy(true)
    try {
      await approveSoulProposal(thread.id)
      toast.success('Soul update applied')
      setDiffOpen(false)
      onResolved?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Approve failed')
    } finally {
      setBusy(false)
    }
  }, [parsed, thread.id, onResolved])

  const handleDismiss = useCallback(async () => {
    setBusy(true)
    try {
      await dismissSoulProposal(thread.id)
      toast.success('Soul proposal dismissed')
      setDiffOpen(false)
      onResolved?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Dismiss failed')
    } finally {
      setBusy(false)
    }
  }, [thread.id, onResolved])

  const handleEdit = useCallback(() => {
    if (!parsed) return
    window.dispatchEvent(
      new CustomEvent('open-document', {
        detail: { documentId: parsed.documentId },
      }),
    )
  }, [parsed])

  const proposedContent = useMemo(() => {
    if (!parsed) return ''
    if (currentContent == null) {
      return parsed.newString
    }
    return applyPatch(currentContent, parsed.oldString, parsed.newString)
  }, [currentContent, parsed])

  if (!parsed) return null

  const original = currentContent ?? parsed.oldString

  return (
    <div
      className={
        variant === 'inline'
          ? 'flex flex-wrap items-center gap-2 rounded border border-sky-500/30 bg-sky-500/10 px-3 py-3'
          : 'flex flex-wrap shrink-0 items-center gap-2 border-b border-border bg-sky-500/10 px-4 py-3'
      }
      data-testid={testIds.inbox.soulProposalBar}
    >
      <span className="text-sm font-medium text-sky-950 dark:text-sky-200">
        Soul proposal:
      </span>
      <button
        type="button"
        onClick={handleEdit}
        data-testid={testIds.inbox.soulProposalDocLink}
        className="max-w-[220px] truncate text-xs font-medium text-primary hover:underline"
        title={parsed.targetPath}
      >
        {parsed.filename}
      </button>
      {parsed.rationale ? (
        <span className="max-w-[200px] truncate text-xs text-muted-foreground" title={parsed.rationale}>
          - {parsed.rationale}
        </span>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="default"
        disabled={busy}
        data-testid={testIds.inbox.soulProposalApprove}
        onClick={() => void handleApprove()}
      >
        Approve
      </Button>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        data-testid={testIds.inbox.soulProposalViewDiff}
        onClick={() => void handleOpenDiff()}
      >
        View Diff
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-testid={testIds.inbox.soulProposalEdit}
        onClick={handleEdit}
      >
        Edit
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={busy}
        data-testid={testIds.inbox.soulProposalDismiss}
        onClick={() => void handleDismiss()}
      >
        Dismiss
      </Button>
      {loadError ? <span className="text-xs text-destructive">{loadError}</span> : null}

      <DiffPreviewDialog
        open={diffOpen}
        onOpenChange={setDiffOpen}
        original={original}
        proposed={proposedContent}
        filename={parsed.filename}
        title="Review soul proposal"
        isApplying={busy}
        onAccept={() => void handleApprove()}
        onReject={() => setDiffOpen(false)}
        onEdit={handleEdit}
      />
    </div>
  )
}

/** True when metadata identifies a soul proposal thread (resolved or not). */
export function isSoulProposalThread(thread: Thread | null | undefined): boolean {
  if (!thread?.metadata) return false
  return isSoulProposalMeta(thread.metadata as SoulProposalMeta)
}

/** True when the inbox thread should show the soul proposal action bar. */
export function isUnresolvedSoulProposal(thread: Thread | null | undefined): boolean {
  if (!isSoulProposalThread(thread) || !thread?.metadata) return false
  const meta = thread.metadata as SoulProposalMeta
  return meta.resolved !== true
}

/** Soul target document for chat context chip when discussing a proposal. */
export function getSoulProposalContextDoc(
  thread: Thread | null | undefined,
): { documentId: string; title: string } | null {
  if (!thread) return null
  const parsed = readMeta(thread)
  if (!parsed) return null
  return { documentId: parsed.documentId, title: parsed.filename || parsed.targetPath }
}
